import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  BASE_STYLE,
  LIGHT_OVERRIDE,
  LIGHT_FIX_SCRIPT,
  DARK_FIX_SCRIPT,
  LINK_TARGET_SCRIPT,
  SELECTION_BRIDGE_SCRIPT,
} from "./html-frame-styles";
import { useProjectStore } from "@/stores/project-store";
import { updateFrame } from "@/api/spaces";
import { isTauri } from "@/lib/is-tauri";
import {
  buildProxyUrl,
  extractEmbedParams,
  isTauriDevHttpOrigin,
  normalizeDirectYoutubeEmbedSrc,
} from "./embed-providers";
import { THEME_CHANGED_EVENT } from "@/lib/theme";
import { htmlAuthoredMotionRuntime } from "./html-authored-motion";
import { resolveHtmlUploadAssets } from "./html-upload-assets";
import { useHtmlPrivateAssets } from "./use-html-private-assets";
import {
  canonicalizeHtmlAssetEdit,
  privateHtmlDocument,
} from "./html-private-document";
import { useNotificationStore } from "@/stores/notification-store";
import {
  CHROMELESS_TRANSPARENT_BACKGROUND_SCRIPT,
  chromelessInsetScript,
} from "./html-frame-chromeless";
import {
  HTML_FRAME_EDIT_MESSAGE,
  annotateEditableHtml,
  applyHtmlEdits,
  htmlEditBase,
  inlineEditScript,
  readHtmlFrameEdit,
  type HtmlFrameEdit,
} from "./html-frame-edits";
import {
  HTML_FRAME_SIZE_MESSAGE,
  flushHtmlFrameEdit,
  framePx,
  isHtmlFrameMessage,
} from "./html-frame-bridge";
import { HTML_FRAME_BRIDGE_SCRIPT } from "./html-frame-runtime";
import { HTML_FRAME_DESIGN_SCRIPT } from "./html-frame-design-runtime";
import { applyFramePatch } from "@/lib/frame-ingest";

export type HtmlFrameThemeMode = "app" | "light" | "dark";

const STABLE_IFRAME_LAYOUT: CSSProperties = {
  display: "block",
  width: "100%",
  height: "100%",
  minWidth: 0,
  maxWidth: "100%",
  border: 0,
  contain: "strict",
};

// The srcDoc element's color scheme must match the scheme its document
// declares; otherwise WebView2 paints an opaque backdrop behind a
// transparent document. A style change does not reload the srcDoc.
const DARK_SRC_DOC_IFRAME_LAYOUT: CSSProperties = {
  ...STABLE_IFRAME_LAYOUT,
  colorScheme: "dark",
};

const LIGHT_SRC_DOC_IFRAME_LAYOUT: CSSProperties = {
  ...STABLE_IFRAME_LAYOUT,
  colorScheme: "light",
};

function readAppThemeIsDark(): boolean {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
}

function dbg(msg: string) {
  if (!isTauri()) return;
  void import("@tauri-apps/api/core")
    .then(({ invoke }) => invoke("dbg_log", { msg: `[embed] ${msg}` }))
    .catch(() => {});
}

export interface EditableHtmlIframeHandle {
  /** Persists pending inline edits from the frame document. */
  commitCurrentDocument: () => Promise<void>;
}

interface EditableHtmlIframeProps {
  frameId: string;
  html: string;
  /**
   * If set, the iframe renders `embedSrc` directly via `src`. Used for
   * single-iframe wrapper content (YouTube, etc.) to avoid double-nesting
   * which browsers block.
   */
  embedSrc?: string | null;
  /** Iframe `title` attribute. */
  title?: string;
  /** Tailwind classes for the iframe element. */
  className?: string;
  /** `data-testid` for the iframe element. */
  dataTestId?: string;
  /** Show iframe document scrollbars; hidden, the document still scrolls. */
  showScrollbars?: boolean;
  /** Override the iframe document theme for this render only. */
  themeMode?: HtmlFrameThemeMode;
  /** Enables inline text editing inside the iframe document. */
  editable?: boolean;
  /**
   * Adds focus-mode breathing room inside generated HTML documents so the
   * iframe background still reaches the frame edges.
   */
  chromelessInsetBlockPx?: number;
  /** Makes the iframe document root/body transparent. */
  transparentBackground?: boolean;
  /**
   * Inject `SELECTION_BRIDGE_SCRIPT` so text selections in the iframe are
   * relayed to the parent window via `postMessage`. Used by the expanded
   * view to power the selection toolbar.
   */
  selectionBridge?: boolean;
  /** One-shot content revision that stages blocks and draws inline SVG. */
  authoredMotionRevision?: number;
  onHtmlCommit?: (html: string) => void;
  /** The frame document reported its scrollable content size. */
  onContentSize?: (size: { width: number; height: number }) => void;
}

/**
 * Renders agent-produced HTML in a sandboxed iframe with optional inline
 * text editing. When an edited element blurs (or the app flushes before
 * saving), the frame reports the changed elements and the edits are applied
 * to the authored HTML (see html-frame-edits.ts) and persisted.
 */
export const EditableHtmlIframe = forwardRef<
  EditableHtmlIframeHandle,
  EditableHtmlIframeProps
>(function EditableHtmlIframe(
  {
    frameId,
    html,
    embedSrc,
    title,
    className,
    dataTestId,
    showScrollbars = true,
    themeMode = "app",
    editable = false,
    chromelessInsetBlockPx = 0,
    transparentBackground = false,
    selectionBridge = false,
    authoredMotionRevision = 0,
    onHtmlCommit,
    onContentSize,
  },
  ref,
) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [appIsDark, setAppIsDark] = useState(readAppThemeIsDark);
  const editBase = useMemo(() => htmlEditBase(html), [html]);
  const assetHtml = useMemo(
    () => resolveHtmlUploadAssets(editable ? annotateEditableHtml(html) : html),
    [editable, html],
  );
  const { script: privateAssetScript, canonicalize } = useHtmlPrivateAssets(
    iframeRef,
    assetHtml,
    !embedSrc,
  );
  const trustedOnly = Boolean(privateAssetScript);
  const privateNonce = useMemo(
    () => (trustedOnly ? crypto.randomUUID().replace(/-/g, "") : ""),
    [trustedOnly],
  );

  const persistHtml = useCallback(
    (newHtml: string) => {
      try {
        newHtml = canonicalizeHtmlAssetEdit(html, newHtml, canonicalize);
      } catch (error) {
        useNotificationStore.getState().addSystemToast({
          id: `private-image-edit-${frameId}`,
          level: "error",
          title:
            error instanceof Error
              ? error.message
              : "Image could not be saved.",
        });
        return;
      }
      if (newHtml === html) return;
      if (onHtmlCommit) {
        onHtmlCommit(newHtml);
        return;
      }
      const { currentProject, currentSpace } = useProjectStore.getState();
      if (!currentProject || !currentSpace) return;
      applyFramePatch(frameId, { content: { html: newHtml } });
      updateFrame(currentProject.id, currentSpace.id, frameId, {
        content: { html: newHtml },
      })
        .then((updatedFrame) => {
          applyFramePatch(frameId, updatedFrame, { origin: "server" });
        })
        .catch(() => {});
    },
    [frameId, html, onHtmlCommit, canonicalize],
  );

  // Edits name elements of the HTML the frame was built from; one from an
  // older document (the frame reloads when its HTML changes) is dropped.
  const commitEdit = useCallback(
    (edit: HtmlFrameEdit | null) => {
      if (edit?.base === editBase) persistHtml(applyHtmlEdits(html, edit));
    },
    [editBase, html, persistHtml],
  );

  useImperativeHandle(
    ref,
    () => ({
      commitCurrentDocument: async () => {
        const iframe = iframeRef.current;
        if (!iframe || !editable) return;
        commitEdit(await flushHtmlFrameEdit(iframe));
      },
    }),
    [commitEdit, editable],
  );

  useEffect(() => {
    const el = document.documentElement;
    const syncTheme = () => {
      setAppIsDark(readAppThemeIsDark());
    };
    window.addEventListener(THEME_CHANGED_EVENT, syncTheme);
    const observer = new MutationObserver(syncTheme);
    observer.observe(el, {
      attributes: true,
      attributeFilter: ["class", "data-theme-choice"],
    });
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, syncTheme);
      observer.disconnect();
    };
  }, []);

  // Messages from the frame document. Only its own window is trusted as a
  // sender, and an edit is accepted only while inline editing is on.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!isHtmlFrameMessage(e, iframe)) return;
      if (e.data?.type === HTML_FRAME_SIZE_MESSAGE) {
        const width = framePx(e.data.width);
        const height = framePx(e.data.height);
        if (width === null || height === null) return;
        const size = { width: Math.max(0, width), height: Math.max(0, height) };
        iframe.dataset.contentWidth = String(size.width);
        iframe.dataset.contentHeight = String(size.height);
        onContentSize?.(size);
        return;
      }
      if (!editable || e.data?.type !== HTML_FRAME_EDIT_MESSAGE) return;
      commitEdit(readHtmlFrameEdit(e.data.edit));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [commitEdit, editable, onContentSize]);

  if (embedSrc) {
    const finalSrc = normalizeEmbedSrc(embedSrc);
    dbg(`render frameId=${frameId} finalSrc=${finalSrc}`);
    return (
      <iframe
        key="embed"
        src={finalSrc}
        // Cross-origin embed (see detectEmbedSrc): it keeps its own origin
        // but may not navigate the app window.
        sandbox="allow-scripts allow-same-origin allow-forms allow-presentation allow-popups allow-popups-to-escape-sandbox"
        className={className}
        style={STABLE_IFRAME_LAYOUT}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="origin"
        title={title}
        data-testid={dataTestId}
        onLoad={(e) => {
          const el = e.currentTarget;
          let innerOrigin = "unknown";
          try {
            innerOrigin = el.contentWindow?.location?.href ?? "no-href";
          } catch (err) {
            innerOrigin = `cross-origin-block: ${(err as Error).message}`;
          }
          dbg(
            `iframe.onLoad frameId=${frameId} src=${el.src} contentHref=${innerOrigin}`,
          );
        }}
        onError={(e) => {
          dbg(
            `iframe.onError frameId=${frameId} src=${(e.currentTarget as HTMLIFrameElement).src}`,
          );
        }}
      />
    );
  }

  const chromeless = chromelessInsetBlockPx > 0;
  const extraScripts =
    privateAssetScript +
    (authoredMotionRevision > 0
      ? htmlAuthoredMotionRuntime(authoredMotionRevision)
      : "") +
    chromelessInsetScript(chromelessInsetBlockPx) +
    (chromeless || transparentBackground
      ? CHROMELESS_TRANSPARENT_BACKGROUND_SCRIPT
      : "") +
    LINK_TARGET_SCRIPT +
    HTML_FRAME_BRIDGE_SCRIPT +
    HTML_FRAME_DESIGN_SCRIPT +
    (editable ? inlineEditScript(editBase) : "") +
    (selectionBridge ? SELECTION_BRIDGE_SCRIPT : "");
  const isDark = themeMode === "app" ? appIsDark : themeMode === "dark";
  const trustedHtml = isDark
    ? BASE_STYLE +
      thinScrollbarStyle(isDark) +
      scrollbarVisibilityStyle(showScrollbars) +
      DARK_FIX_SCRIPT +
      extraScripts
    : BASE_STYLE +
      LIGHT_OVERRIDE +
      thinScrollbarStyle(isDark) +
      scrollbarVisibilityStyle(showScrollbars) +
      LIGHT_FIX_SCRIPT +
      extraScripts;
  const themedHtml = trustedOnly
    ? privateHtmlDocument(trustedHtml, assetHtml, privateNonce)
    : trustedHtml + assetHtml;

  return (
    <iframe
      key="srcdoc"
      ref={iframeRef}
      srcDoc={themedHtml}
      // Never add allow-same-origin: with allow-scripts it would let the
      // agent-written document script the app window (auth, Tauri IPC).
      sandbox={
        trustedOnly
          ? "allow-scripts allow-downloads"
          : "allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox"
      }
      className={className}
      style={isDark ? DARK_SRC_DOC_IFRAME_LAYOUT : LIGHT_SRC_DOC_IFRAME_LAYOUT}
      title={title}
      data-testid={dataTestId}
      data-authored-motion-revision={authoredMotionRevision}
    />
  );
});

function thinScrollbarStyle(isDark: boolean): string {
  const thumb = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.18)";
  const thumbHover = isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.28)";
  const thumbActive = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.38)";

  return `<style>
html{
  scrollbar-width:thin;
  scrollbar-color:transparent transparent;
}
html:hover,
html:focus-within{
  scrollbar-color:${thumb} transparent;
}
html::-webkit-scrollbar{
  width:7px;
  height:7px;
}
html::-webkit-scrollbar-track{
  background:transparent;
}
html::-webkit-scrollbar-button,
html::-webkit-scrollbar-corner{
  display:none;
  background:transparent;
}
html::-webkit-scrollbar-thumb{
  background-color:transparent;
  border:2px solid transparent;
  border-radius:999px;
  background-clip:content-box;
}
html:hover::-webkit-scrollbar-thumb,
html:focus-within::-webkit-scrollbar-thumb{
  background-color:${thumb};
}
html::-webkit-scrollbar-thumb:hover{
  background-color:${thumbHover};
}
html::-webkit-scrollbar-thumb:active{
  background-color:${thumbActive};
}
body,
body *{
  scrollbar-width:none;
}
body::-webkit-scrollbar,
body *::-webkit-scrollbar{
  width:0!important;
  height:0!important;
  display:none!important;
}
</style>`;
}

function scrollbarVisibilityStyle(showScrollbars: boolean): string {
  if (showScrollbars) return "";
  return `<style>
html,body{
  scrollbar-width:none!important;
  -ms-overflow-style:none!important;
}
html::-webkit-scrollbar,
body::-webkit-scrollbar,
*::-webkit-scrollbar{
  width:0!important;
  height:0!important;
  display:none!important;
}
</style>`;
}

/** The unsandboxed embed iframe may only load another site over http(s). */
function isCrossOriginHttpUrl(src: string): boolean {
  try {
    const url = new URL(src, window.location.href);
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      url.origin !== window.location.origin
    );
  } catch {
    return false;
  }
}

/**
 * Detects HTML content that is just a wrapper around a single `<iframe>`
 * (e.g. YouTube/Vimeo embeds). Returns the iframe `src` if so, otherwise
 * `null`. Rendering such content as a srcDoc would create a double-nested
 * iframe which browsers block for embed providers. Anything but a
 * cross-origin http(s) page stays in the sandboxed srcDoc document.
 */
export function detectEmbedSrc(html: string): string | null {
  const embedMatch = html.match(/<iframe[^>]+src="([^"]+)"[^>]*>/i);
  const residue = html.replace(/<\/?(?:div|iframe)[^>]*>/gi, "").trim();
  const src = embedMatch && !residue ? (embedMatch[1] ?? null) : null;
  const result = src && isCrossOriginHttpUrl(src) ? src : null;
  dbg(
    `detectEmbedSrc match=${!!embedMatch} residueLen=${residue.length} result=${result ?? "null"} htmlPrefix=${html.slice(0, 200).replace(/\n/g, " ")}`,
  );
  return result;
}

export function normalizeEmbedSrc(src: string): string {
  if (isTauri() && !isTauriDevHttpOrigin()) {
    const parsed = extractEmbedParams(src);
    if (parsed) {
      const proxy = buildProxyUrl(parsed);
      dbg(
        `normalizeEmbedSrc tauri provider=${parsed.provider} id=${parsed.id} src=${src} -> ${proxy}`,
      );
      return proxy;
    }
    dbg(`normalizeEmbedSrc tauri NO_MATCH src=${src} (returning original)`);
    return src;
  }

  return normalizeDirectYoutubeEmbedSrc(src);
}

export default EditableHtmlIframe;
